import React, { useState, useEffect } from 'react';
import { Button, Table, Input, Form, Typography, message, Layout, Row, Col, Card, Space, Modal, Popconfirm, Switch, InputNumber, Select } from 'antd';
import { SearchOutlined, DeleteOutlined, PlusOutlined, EditOutlined, SettingOutlined } from '@ant-design/icons';
import {
  ParamSearchRequest,
  ParamSearchResponseItem,
  ParamCreateRequest,
  ParamUpdateRequest,
  ParamDeleteRequest
} from "../../../model/system";
import {baseDataService, paramService} from "../../../services";
import {ParamTypeResponse} from "../../../model";
import { getPageSizeOptions, resetPaginationByTotal } from 'utils/pageUtil';

const { Title } = Typography;
const { Content } = Layout;
const { Option } = Select;

const ParameterPage: React.FC = () => {
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [parameters, setParameters] = useState<ParamSearchResponseItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [currentParameter, setCurrentParameter] = useState<ParamSearchResponseItem | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  
  // 取得參數類型清單
  const [paramTypes, setParamTypes] = useState<ParamTypeResponse[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);

  // 搜尋參數
  const handleSearch = async (values: any) => {
    setLoading(true);
    try {
      // 構建搜尋請求
      const searchRequest: ParamSearchRequest = {
        paramType: values.paramType,
        paramCode: values.paramCode,
        paramValue: values.paramValue,
        isActive: values.isActive
      };

      // 調用API搜尋參數
      const result = await paramService.search(searchRequest);

      if (result) {
        setParameters(result);
        setTotalRecords(result.length);
        // 重置到第一頁
        setPagination(resetPaginationByTotal(result.length));
        
        // 如果沒有返回數據，顯示通知
        if (result.length === 0) {
          message.info('查無資料');
        }

      } else {
        // 如果返回的結果為null（可能是由於網絡錯誤或其他問題）
        setParameters([]);
        setTotalRecords(0);
        message.error('查詢失敗，請稍後重試');
      }
    } catch (error) {
      console.error('搜尋參數失敗:', error);
      message.error('搜尋失敗，請稍後重試');
      setParameters([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // 重置表單
  const handleReset = () => {
    form.resetFields();
  };

  // 顯示新增參數對話框
  const showCreateModal = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      isActive: true,
      sortOrder: 0
    });
    setIsCreateModalVisible(true);
  };

  // 關閉新增參數對話框
  const handleCreateCancel = () => {
    setIsCreateModalVisible(false);
  };

  // 處理新增參數
  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      
      const createRequest: ParamCreateRequest = {
        params: [{
          paramType: values.paramType,
          paramCode: values.paramCode,
          paramValue: values.paramValue,
          sortOrder: values.sortOrder || 0,
          isActive: values.isActive,
          remarks: values.remarks,
          createdBy: 'tami'
        }]
      };
      
      const result = await paramService.createParameters(createRequest);
      
      if (result && result.createdCount > 0) {
        message.success('參數新增成功');
        setIsCreateModalVisible(false);
        // 取得當前搜尋條件
        const currentSearchConditions = form.getFieldsValue();
        // 重新載入資料
        handleSearch(currentSearchConditions);
      } else {
        message.error('參數新增失敗');
      }
    } catch (error) {
      console.error('新增參數時發生錯誤:', error);
      message.error('表單驗證失敗或新增過程發生錯誤');
    } finally {
      setCreateLoading(false);
    }
  };

  // 處理編輯參數
  const handleEdit = (record: ParamSearchResponseItem) => {
    setCurrentParameter(record);
    editForm.setFieldsValue({
      paramType: record.paramType,
      paramCode: record.paramCode,
      paramValue: record.paramValue,
      sortOrder: record.sortOrder,
      isActive: record.isActive,
      remarks: record.remarks
    });
    setIsEditModalVisible(true);
  };

  // 關閉編輯參數對話框
  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setCurrentParameter(null);
  };

  // 提交編輯參數
  const handleEditSubmit = async () => {
    if (!currentParameter) return;
    
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      
      // 構建編輯請求
      const editRequest: ParamUpdateRequest = {
        params: [{
          systemParameterId: currentParameter.systemParameterId,
          paramType: values.paramType,
          paramCode: values.paramCode,
          paramValue: values.paramValue,
          sortOrder: values.sortOrder || 0,
          isActive: values.isActive,
          remarks: values.remarks,
          modifiedBy: 'tami'
        }]
      };
      
      const result = await paramService.updateParameters(editRequest);
      
      if (result && result.updatedCount > 0) {
        message.success('參數修改成功');
        setIsEditModalVisible(false);
        // 取得當前搜尋條件
        const currentSearchConditions = form.getFieldsValue();
        // 重新載入資料
        handleSearch(currentSearchConditions);
      } else {
        message.error('參數修改失敗');
      }
    } catch (error) {
      console.error('修改參數時發生錯誤:', error);
      message.error('表單驗證失敗或修改過程發生錯誤');
    } finally {
      setEditLoading(false);
    }
  };

  // 處理刪除參數
  const handleDelete = async (record: ParamSearchResponseItem) => {
    try {
      const deleteRequest: ParamDeleteRequest = {
        paramIds: [{
          systemParameterId: record.systemParameterId
        }]
      };
      
      const result = await paramService.deleteParameters(deleteRequest);
      
      if (result && result.deletedCount > 0) {
        message.success('參數刪除成功');
        // 取得當前搜尋條件
        const currentSearchConditions = form.getFieldsValue();
        // 重新載入資料
        handleSearch(currentSearchConditions);
      } else {
        message.error('參數刪除失敗');
      }
    } catch (error) {
      console.error('刪除參數時發生錯誤:', error);
      message.error('刪除過程發生錯誤');
    }
  };

  // 處理分頁、篩選和排序變更
  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    const newPagination = {
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    };
    
    setPagination(newPagination);
  };

  // 載入參數類型列表
  const loadParamTypes = async (searchConditions = {}) => {
    setTypesLoading(true);
    try {
      const paramTypes = await baseDataService.getParamTypes();
      
      if (paramTypes) {
        setParamTypes(paramTypes);
      }
    } catch (error) {
      console.error('載入參數類型列表失敗:', error);
    } finally {
      setTypesLoading(false);
    }
  };

  useEffect(() => {
    loadParamTypes();
  }, []);

  // 初始加載
  useEffect(() => {
    // 初始搜尋所有參數
    const initialSearchConditions = {};
    handleSearch(initialSearchConditions);
  }, []);

  // 表格列定義
  const columns = [
    {
      title: '序號',
      dataIndex: 'systemParameterId',
      key: 'systemParameterId',
      width: 100
    },
    {
      title: '參數類型',
      dataIndex: 'paramType',
      key: 'paramType',
      width: 150
    },
    {
      title: '參數代碼',
      dataIndex: 'paramCode',
      key: 'paramCode',
      width: 150
    },
    {
      title: '參數值',
      dataIndex: 'paramValue',
      key: 'paramValue',
      width: 200
    },
    {
      title: '啟用狀態',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <span>
          {isActive ? 
            <span style={{ color: 'green' }}>啟用</span> : 
            <span style={{ color: 'red' }}>停用</span>}
        </span>
      )
    },
    {
      title: '備註',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
      render: (text: string) => text || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (text: string, record: ParamSearchResponseItem) => (
        <Space>
          <Button 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="編輯"
          />
          <Popconfirm
            title="確定要刪除此參數嗎？"
            onConfirm={() => handleDelete(record)}
            okText="確定"
            cancelText="取消"
          >
            <Button 
              danger 
              icon={<DeleteOutlined />}
              title="刪除"
            />
          </Popconfirm>
        </Space>
      ),
    }
  ];

  return (
    <Layout style={{ minHeight: '100%' }}>
      <Content>
        {/* 頁面標題 */}
        <Row justify="center" style={{ marginBottom: 20 }}>
          <Col>
            <Title level={3}>
              <span style={{ marginRight: 8 }}><SettingOutlined /></span> 參數設定
            </Title>
          </Col>
        </Row>

        {/* 搜尋表單 */}
        <Card
          title={
            <Space>
              <SearchOutlined />
              <span>搜尋條件</span>
            </Space>
          }
          style={{ marginBottom: 20 }}
        >
          <Form
            form={form}
            name="paramSearch"
            onFinish={handleSearch}
            layout="vertical"
          >
            <Row gutter={[24, 0]}>
              {/* 參數類型 */}
              <Col xs={24} md={8}>
                <Form.Item label="參數類型" name="paramType">
                  <Select 
                    placeholder="選擇參數類型" 
                    allowClear
                    loading={typesLoading}
                    showSearch
                    optionFilterProp="children"
                  >
                    {paramTypes.map(type => (
                      <Option key={type.paramType} value={type.paramType}>{type.remarks}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              
              {/* 參數代碼 */}
              <Col xs={24} md={8}>
                <Form.Item label="參數代碼" name="paramCode">
                  <Input placeholder="輸入參數代碼" />
                </Form.Item>
              </Col>
              
              {/* 參數值 */}
              <Col xs={24} md={8}>
                <Form.Item label="參數值" name="paramValue">
                  <Input placeholder="輸入參數值" />
                </Form.Item>
              </Col>
              
              {/* 啟用狀態 */}
              <Col xs={24} md={8}>
                <Form.Item label="啟用狀態" name="isActive">
                  <Select placeholder="選擇啟用狀態" allowClear>
                    <Option value={true}>啟用</Option>
                    <Option value={false}>停用</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* 按鈕區域 */}
              <Col xs={24}>
                <Row justify="center" gutter={[16, 0]}>
                  <Col>
                    <Button 
                      onClick={handleReset}
                      icon={<DeleteOutlined />}
                    >
                      清除
                    </Button>
                  </Col>
                  <Col>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      icon={<SearchOutlined />}
                    >
                      查詢
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* 搜尋結果區域 */}
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>搜尋結果: {totalRecords} 筆記錄</span>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={showCreateModal}
              >
                新增參數
              </Button>
            </div>
          }
        >
          <Table
            rowKey="systemParameterId"
            dataSource={parameters}
            columns={columns}
            loading={loading}
            pagination={{
              ...pagination,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: getPageSizeOptions(totalRecords),
              showTotal: (total) => `共 ${total} 筆`
            }}
            onChange={handleTableChange}
            scroll={{ x: 1300 }}
          />
        </Card>

        {/* 新增參數對話框 */}
        <Modal
          title="新增參數"
          open={isCreateModalVisible}
          onCancel={handleCreateCancel}
          footer={[
            <Button key="cancel" onClick={handleCreateCancel}>
              取消
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              loading={createLoading} 
              onClick={handleCreate}
            >
              確定
            </Button>,
          ]}
          width={600}
        >
          <Form
            form={createForm}
            layout="vertical"
            name="createParameterForm"
            initialValues={{ isActive: true, sortOrder: 0 }}
          >
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name="paramType"
                  label="參數類型"
                  rules={[
                    { required: true, message: '請輸入參數類型' },
                    { max: 50, message: '參數類型不能超過50個字元' }
                  ]}
                >
                  <Input placeholder="請輸入參數類型" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  name="paramCode"
                  label="參數代碼"
                  rules={[
                    { required: true, message: '請輸入參數代碼' },
                    { max: 50, message: '參數代碼不能超過50個字元' }
                  ]}
                >
                  <Input placeholder="請輸入參數代碼" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  name="paramValue"
                  label="參數值"
                  rules={[
                    { required: true, message: '請輸入參數值' },
                    { max: 255, message: '參數值不能超過255個字元' }
                  ]}
                >
                  <Input placeholder="請輸入參數值" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  name="sortOrder"
                  label="排序順序"
                  rules={[
                    { required: true, message: '請輸入排序順序' }
                  ]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="請輸入排序順序" />
                </Form.Item>
              </Col>
              
              <Col span={24}>
                <Form.Item
                  name="isActive"
                  label="啟用狀態"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                </Form.Item>
              </Col>
              
              <Col span={24}>
                <Form.Item
                  name="remarks"
                  label="備註"
                >
                  <Input.TextArea rows={4} placeholder="請輸入備註" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* 編輯參數對話框 */}
        <Modal
          title="修改參數"
          open={isEditModalVisible}
          onCancel={handleEditCancel}
          footer={[
            <Button key="cancel" onClick={handleEditCancel}>
              取消
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              loading={editLoading} 
              onClick={handleEditSubmit}
            >
              確定
            </Button>,
          ]}
          width={600}
        >
          <Form
            form={editForm}
            layout="vertical"
            name="editParameterForm"
          >
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name="paramType"
                  label="參數類型"
                  rules={[
                    { required: true, message: '請輸入參數類型' },
                    { max: 50, message: '參數類型不能超過50個字元' }
                  ]}
                >
                  <Input placeholder="請輸入參數類型" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  name="paramCode"
                  label="參數代碼"
                  rules={[
                    { required: true, message: '請輸入參數代碼' },
                    { max: 50, message: '參數代碼不能超過50個字元' }
                  ]}
                >
                  <Input placeholder="請輸入參數代碼" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  name="paramValue"
                  label="參數值"
                  rules={[
                    { required: true, message: '請輸入參數值' },
                    { max: 255, message: '參數值不能超過255個字元' }
                  ]}
                >
                  <Input placeholder="請輸入參數值" />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  name="sortOrder"
                  label="排序順序"
                  rules={[
                    { required: true, message: '請輸入排序順序' }
                  ]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="請輸入排序順序" />
                </Form.Item>
              </Col>
              
              <Col span={24}>
                <Form.Item
                  name="isActive"
                  label="啟用狀態"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                </Form.Item>
              </Col>
              
              <Col span={24}>
                <Form.Item
                  name="remarks"
                  label="備註"
                >
                  <Input.TextArea rows={4} placeholder="請輸入備註" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};

export default ParameterPage;